import React, {
  useState,
  useEffect,
} from 'react';
import {
  AxiosError,
} from 'axios';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import {
  CheckEligibilityResponseDTO,
  InsuranceDTO,
  PatientDTO,
} from 'dtos';
import {
  useMixpanelTrack,
} from 'core/hooks';
import dayjs from 'utils/dayjs';
import {
  formatDate,
} from 'utils/date';
import ToolTip from 'core/components/Tooltip';
import Button from 'pages/Dashboard/components/Button';
import {
  useCheckEligibility,
} from 'views/EMR/components/Insurance/hooks';
import FixEligibilityDialog, {
  FixEligibilityDialogParams,
} from 'pages/Dashboard/components/FixEligibilityDialog';
import Spinner from 'pages/Dashboard/components/Spinner';
import {
  insurancesEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import EligibilityInformationDialog, {
  EligibilityInformationDialogProps,
} from 'views/EMR/components/Eligibility/EligibilityInformationDialog';
import eligibilityStateConfig from 'views/EMR/components/Eligibility/eligibilityStateConfig';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import {
 notEmptyString,
} from '@populateui/utils';

type Part = 'root' | 'lastChecked';

type Props = {
  insurance: InsuranceDTO;
  patient?: PatientDTO;
  startDate?: string;
  endDate?: string;
  disabled?: boolean;
  classes?: Partial<Record<Part, string>>;
  tooltip?: string;
  shortView?: boolean;
};

export default function InsuranceEligibility({
  insurance,
  patient,
  startDate,
  endDate,
  disabled: _disabled = false,
  classes,
  shortView = false,
  tooltip: _tooltip,
}: Props) {
  const {
    insuranceId,
    insuranceName,
    payerEntry,
  } = insurance;

  const {
    eligibility: supportsEligibility = true,
    eligibilityMessage,
  } = payerEntry?.ediSupport ?? {};

  const disabled = _disabled || !supportsEligibility;
  const tooltip = notEmptyString(eligibilityMessage)
    ? eligibilityMessage
    : _tooltip;

  const {
    isPending: isLoading,
    mutateAsync: checkEligibility,
  } = useCheckEligibility();
  const [
    lastEligibilityCheckData,
    setLastEligibilityCheckData,
  ] = useState<CheckEligibilityResponseDTO>();
  const hasInsuranceId = !isNil(insuranceId) && insuranceId !== 0;
  const [fixDialogParams, setFixDialogParams] = useState<FixEligibilityDialogParams | null>(null);
  const [
    eligibilityDialogParams,
    setEligibilityDialogParams,
  ] = useState<EligibilityInformationDialogProps | null>(null);

  const mixPanelTrack = useMixpanelTrack();

  const { lastChecked } = lastEligibilityCheckData ?? {};
  const eligibilityState = lastEligibilityCheckData?.eligibilityState?.state;
  const eligibilityStateDescription = lastEligibilityCheckData?.eligibilityState?.description;
  const stateConfig = !isNil(eligibilityState) ? eligibilityStateConfig[eligibilityState] : null;

  const showEligibilityData = (data: CheckEligibilityResponseDTO) => {
    setEligibilityDialogParams({
      ...data,
      patient,
      insuranceName,
    });
  };

  const onEligibilityCheck = async (checkType: 'Current' | 'Last' = 'Current', show = true) => {
    if (show) {
      mixPanelTrack('onEligibilityClick');
    }
    try {
      const data = await checkEligibility({
        insuranceId,
        patientId: patient?.patientId,
        startDate: dayjs(startDate).startOf('day').format(),
        endDate: dayjs(endDate).endOf('day').format(),
        checkType,
      });
      if (checkType === 'Current') {
        insurancesEventChannel.emit('onEligibilityCheckRefresh');
      }
      if (show) {
        showEligibilityData(data);
      }
      setLastEligibilityCheckData(data);
      mixPanelTrack('onEligibilityResult', { eligibilityStatus: 'Success' });
    } catch (e) {
      if ((e as AxiosError)?.response?.status === 403) {
        setFixDialogParams({
          insurance,
          patientId: patient?.patientId ?? 0,
        });
        if (show) {
          mixPanelTrack('onEligibilityResult', { eligibilityStatus: 'Insurance Error' });
        }
      } else {
        mixPanelTrack('onEligibilityResult', { eligibilityStatus: 'Error' });
      }
    }
  };

  useEffect(() => {
    if (hasInsuranceId) {
      onEligibilityCheck('Last', false);
    }
  }, [
    hasInsuranceId,
    insuranceId,
    patient,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    setLastEligibilityCheckData(undefined);
  }, [insuranceId]);

  return (
    <>
      <div className={clsx('flex items-center space-x-2', classes?.root)}>
        {(
          hasInsuranceId
          && !isEmpty(lastEligibilityCheckData)
          && !isNil(lastChecked)
        ) && (
          <div className={clsx('flex items-center space-x-2', classes?.lastChecked)}>
            <Button
              className="!underline"
              variant="text"
              color="primary"
              onClick={() => { showEligibilityData(lastEligibilityCheckData); }}
            >
              Last Checked
              {' '}
              {formatDate(lastChecked ?? '')}
            </Button>
            <ToolTip title={eligibilityStateDescription} hide={isNil(eligibilityStateDescription)}>
              <span className="flex items-center">
                {!isLoading && !isNil(stateConfig) && (
                  <stateConfig.Icon className={stateConfig.className} />
                )}
                {isLoading && <Spinner className="mr-1" size={16} />}
              </span>
            </ToolTip>
          </div>
        )}
        <ToolTip
          title={tooltip ?? (
            <p>
              Insurance needs to be selected
              <br />
              before checking eligibility
            </p>
          )}
          hide={isNil(tooltip) && hasInsuranceId}
        >
          <span>
            <Button
              className={clsx('order-2', {
                '!cursor-not-allowed !pointer-events-auto': disabled || !hasInsuranceId,
                '!border-none !p-2 !rounded-full': shortView,
                '!text-blue-500': shortView && !disabled && !isLoading,
                '!border-secondary !px-2.5 !py-0.5 disabled:!border-gray-300': !shortView,
              })}
              variant="outlined"
              color="primary"
              onClick={() => { onEligibilityCheck(); }}
              disabled={isLoading || !hasInsuranceId || disabled}
            >
              {shortView ? <FactCheckOutlinedIcon className="!w-4 !h-4" /> : 'Check Eligibility'}
            </Button>
          </span>
        </ToolTip>
      </div>
      {!isNil(fixDialogParams) && (
        <FixEligibilityDialog
          {...fixDialogParams}
          onClose={() => { setFixDialogParams(null); }}
        />
      )}
      {!isNil(eligibilityDialogParams) && (
        <EligibilityInformationDialog
          {...eligibilityDialogParams}
          onClose={() => { setEligibilityDialogParams(null); }}
        />
      )}
    </>
  );
}
